// "Onde você trabalhou?" — contrato ou centro de custo, ESCREVENDO para achar.
//
// Pedido do Lucas (28/09/2026): todo dropdown filtra pelo que se digita, como
// a lista de atividades. O <select> nativo do atalho não filtrava — com 80
// contratos a pessoa rolava a lista inteira atrás do número.
//
// Mesmo agrupamento das outras telas de horas (montarOpcoesDestino): meus
// contratos, "não é projeto" e o resto. A busca ignora acento e maiúscula e
// casa número, título e nome do centro de custo.
//
// HTML inline, sem antd, como o AtividadeCampo: o atalho do cabeçalho é todo
// HTML. A lista é position:fixed (o painel do atalho corta o que transborda).
import { useEffect, useId, useRef, useState } from 'react';
import { filtrarDestinos, type GrupoDestino, type OpcaoDestino } from './destinos';

export default function DestinoCampo({
  valor, grupos, onEscolher, placeholder = 'Onde você trabalhou? (escreva para buscar)', compacto = false,
}: {
  valor: string;
  grupos: GrupoDestino[];
  onEscolher: (v: string) => void;
  placeholder?: string;
  compacto?: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [marcado, setMarcado] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const idLista = useId();

  const escolhido = grupos.flatMap(g => g.options).find(o => o.value === valor) || null;
  const filtrados = filtrarDestinos(grupos, busca);
  const planos = filtrados.flatMap(g => g.options);

  useEffect(() => { setMarcado(0); }, [busca]);

  useEffect(() => {
    if (!aberto) return;
    const reposicionar = () => {
      const el = caixa.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const altura = 300;
      const top = window.innerHeight - r.bottom < altura && r.top > altura ? r.top - altura - 4 : r.bottom + 4;
      const width = Math.min(Math.max(r.width, 260), window.innerWidth - 24);
      const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12));
      setPos({ top, left, width });
    };
    reposicionar();
    window.addEventListener('scroll', reposicionar, true);
    window.addEventListener('resize', reposicionar);
    return () => {
      window.removeEventListener('scroll', reposicionar, true);
      window.removeEventListener('resize', reposicionar);
    };
  }, [aberto]);

  function escolher(o: OpcaoDestino) {
    onEscolher(o.value);
    setAberto(false);
    setBusca('');
    entrada.current?.blur();
  }

  const fs = compacto ? 12 : 14;

  return (
    <div ref={caixa} style={{ position: 'relative' }}>
      <input
        ref={entrada}
        // Fechado mostra o escolhido; aberto mostra o que a pessoa escreve.
        value={aberto ? busca : escolhido ? escolhido.label : ''}
        placeholder={aberto && escolhido ? escolhido.label : placeholder}
        onFocus={() => { setAberto(true); setBusca(''); }}
        onClick={() => setAberto(true)}
        // Atraso curto: o clique numa opção chega depois do blur.
        onBlur={() => setTimeout(() => { setAberto(false); setBusca(''); }, 150)}
        onChange={e => { setBusca(e.target.value); setAberto(true); }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setMarcado(m => Math.min(m + 1, planos.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setMarcado(m => Math.max(m - 1, 0)); }
          if (e.key === 'Enter' && aberto && planos[marcado]) { e.preventDefault(); escolher(planos[marcado]); }
          if (e.key === 'Escape') { setAberto(false); setBusca(''); entrada.current?.blur(); }
        }}
        aria-label="Onde você trabalhou"
        role="combobox" aria-expanded={aberto} aria-controls={idLista}
        style={{
          width: '100%', boxSizing: 'border-box', fontSize: fs, padding: compacto ? '6px 24px 6px 8px' : '5px 26px 5px 11px',
          borderRadius: 6, border: `1px solid ${aberto ? '#0930D0' : '#d9d9d9'}`, outline: 'none',
          color: '#222', background: '#fff',
        }} />
      <span aria-hidden style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                                 fontSize: 10, color: '#999', pointerEvents: 'none' }}>▼</span>

      {aberto && pos && (
        <div id={idLista} role="listbox" style={{
          position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 2001,
          background: '#fff', border: '0.5px solid #0930D0', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)', maxHeight: 300, overflowY: 'auto',
        }}>
          {planos.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: '#666', textAlign: 'center' }}>
              {grupos.length === 0 ? 'Carregando a lista…' : `Nada com "${busca}". Tente o número do contrato ou parte do nome.`}
            </div>
          )}
          {filtrados.map(g => (
            <div key={g.label}>
              <div style={{ padding: '6px 10px 2px', fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {g.label}
              </div>
              {g.options.map(o => {
                const i = planos.indexOf(o);
                const ativo = i === marcado;
                return (
                  <button key={o.value} type="button" role="option" aria-selected={o.value === valor}
                    // mousedown e não click: o blur do campo vem antes do click.
                    onMouseDown={e => { e.preventDefault(); escolher(o); }}
                    onMouseEnter={() => setMarcado(i)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                      fontSize: 12, border: 'none', cursor: 'pointer', borderBottom: '0.5px solid #f3f3f3',
                      background: ativo ? '#F0F1F5' : o.value === valor ? '#F0F3FF' : 'transparent',
                      fontWeight: o.value === valor ? 600 : 400,
                    }}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
